import React, { useEffect, useRef } from "react";
import { FaRegCommentAlt } from "react-icons/fa";
import { PiShareFatLight } from "react-icons/pi";
import { profileVerified } from "assets";
import { postedTimeAgo } from "utils/formatTimeSecondToHour";
import { Link } from "react-router-dom";
import TextReaction from "pages/profile-post-contents-page-view/post-component/TextReaction";
import LinkifyText from "../linkify/LinkifyText";
import { faHeart as solidHeart } from "@fortawesome/free-solid-svg-icons";
import { faHeart as outlineHeart } from "@fortawesome/free-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import NewFeedRepostProfileContent from "./new-feed-components/NewFeedRepostProfileContent";
import NewFeedPostImagesColumn from "./new-feed-components/new-feed-post-image-templates/NewFeedPostImagesColumn";
import NewFeedPostImagesBanner from "./new-feed-components/new-feed-post-image-templates/NewFeedPostImagesBanner";
import NewFeedPostImagesDefault from "./new-feed-components/new-feed-post-image-templates/NewFeedPostImagesDefault";
import { useInView } from "react-intersection-observer";
import { PostReaction } from "models/postReaction";
import { RouteUrl, StatusMsg } from "utils";
import { Card } from "components/ui/card";
import { Button } from "components/ui/button";
import NewFeedVideoPlayer from "./new-feed-components/new-feed-video-player/NewFeedVideoPlayer";
import ButtonFollow from "components/app/button-follow/ButtonFollow";
import { t } from "i18next";
import NewFeedPostStatus from "./new-feed-components/new-feed-post-status/NewFeedPostStatus";
import NewFeedSingleImage from "./new-feed-components/new-feed-post-image-templates/NewFeedSignleImage";
import NewFeedSuggestionFriend from "./new-feed-components/new-feed-suggestion-friend/NewFeedSuggestionFriend";
import NewFeedLinkButton from "./new-feed-components/new-feed-link-button/NewFeedLinkButton";
import NewFeedOneComment from "./new-feed-components/new-feed-one-comment/NewFeedOneComment";
import TotalCommentReaction from "pages/profile-post-contents-page-view/post-component/CommentReaction";
import { Service } from "utils/services";
import { useWatchTime } from "store/useWatchTime";
import { Ellipsis } from "lucide-react";
import { AppLog } from "services";
import NewFeedLiveClappr from "./new-feed-components/new-feed-live-clappr/NewFeedLiveClappr";
import ImageUrlProfileStoryAndLive from "../image-url/ImageUrlProfileStoryAndLive";
import NewFeedLiveJW from "./new-feed-components/new-feed-live/NewFeedLive";
import NewFeedLiveFlv from "./new-feed-components/new-feed-live-flv/new_feed_live_flv";

interface PostProps {
  post: any;
  setHeight?: (height: number) => void;
  onOpenReactionDialog: (post: any, postReaction: PostReaction) => void;
  onOpenDialogShare: (post: any) => void;
  onOpenDialogNewFeedPost: (
    post: any,
    video: HTMLVideoElement | null,
    playerControlRef?: React.MutableRefObject<{
      pause: () => void;
      play: () => void;
      goLive: () => void;
    } | null>
  ) => void;
  onOpenPostOption: (e: React.MouseEvent<HTMLButtonElement>, post: any) => void;
  onClickedLike: (post: any) => Promise<void>;
  onClickedFollow: (post: any, userId: any) => Promise<void>;
  onOpenPostModal: (postId: string) => void;
  tmpRouteUrl: string;
}

const NewFeedPostComponent: React.FC<PostProps> = ({
  post,
  setHeight,
  onOpenReactionDialog,
  onOpenDialogShare,
  onOpenDialogNewFeedPost,
  onOpenPostOption,
  onClickedLike,
  onClickedFollow,
  onOpenPostModal,
  tmpRouteUrl,
}) => {
  const { setActivePost, activePost } = useWatchTime();
  const postRef = useRef<HTMLDivElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const { ref: inViewRef, inView } = useInView({ threshold: 0.6 });

  const playerControlRef = useRef<{
    pause: () => void;
    play: () => void;
    goLive: () => void;
  } | null>(null);

  useEffect(() => {
    if (!postRef.current || !setHeight) return;

    const observer = new ResizeObserver(() => {
      if (postRef.current) {
        setHeight(postRef.current.clientHeight);
      }
    });

    observer.observe(postRef.current);

    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    try {
      const video = videoRef.current;
      if (!video) return;

      if (inView) {
        video.play().catch((err) => console.log("Error playing video:", err));
      } else {
        video.pause();
      }
    } catch (error) {
      AppLog.show({
        message: `[Playback failed]`,
        status: StatusMsg.error,
        error,
      });
    }
  }, [inView]);

  // * Track Time
  useEffect(() => {
    const observer = new IntersectionObserver(
      async (entries) => {
        const entry = entries[0];

        if (entry.isIntersecting) {
          // ✅ Ensure only one post is tracked at a time
          if (activePost?.id !== post.id) {
            setActivePost(post);
          }

          try {
            // ✅ Ensure only one video plays at a time
            if (post.type === "video" && videoRef.current) {
              if (activePost?.id !== post.id) {
                await videoRef.current.play();
              }
            }
          } catch (error) {
            AppLog.show({
              message: `[Playback failed]`,
              status: StatusMsg.error,
              error,
            });
          }
        } else {
          if (activePost?.id === post.id) {
            setActivePost(null);
          }

          if (post.type === "video" && videoRef.current) {
            videoRef.current.pause();
          }
        }
      },
      { threshold: 0.9 } // ✅ 90% visibility needed to start tracking
    );

    if (postRef.current) observer.observe(postRef.current);
    return () => {
      if (postRef.current) observer.unobserve(postRef.current);
    };
  }, [post, setActivePost, activePost]);

  const _renderContent = () => (
    <div className="mt-3 ">
      {post.content && <p className="text-muted-foreground font-medium mb-3">{post.content}</p>}

      {post.media_type?.toLowerCase() === "photo" && post.post_attachments?.[0]?.attachment_media_image_file_src && (
        <NewFeedSingleImage post={post} onOpenPostModal={onOpenPostModal} />
      )}

      {post.media_type?.toLowerCase() === "album" &&
        (post.media_template === "Default" ? (
          <NewFeedPostImagesDefault post={post} images={post.post_attachments.map((att: any) => att.attachment_media_image_file_src)} onOpenPostModal={onOpenPostModal} />
        ) : post.media_template === "Column" ? (
          <NewFeedPostImagesColumn post={post} images={post.post_attachments.map((att: any) => att.attachment_media_image_file_src)} onOpenPostModal={onOpenPostModal} />
        ) : (
          <NewFeedPostImagesBanner post={post} images={post.post_attachments.map((att: any) => att.attachment_media_image_file_src)} onOpenPostModal={onOpenPostModal} />
        ))}

      {post.media_type?.toLowerCase() === "video" && (
        <NewFeedVideoPlayer
          src={post.post_attachments?.[0]?.attachment_media_image_file_src}
          thubnail={post?.post_attachments[0]?.attachment_thumbnail_image_file_src}
          attachment={post?.post_attachments[0]}
          videoRefCallback={(el) => {
            videoRef.current = el;
            inViewRef(el);
          }}
        />
      )}

      {post.media_type?.toLowerCase() === "status" && <NewFeedPostStatus post={post} />}

      {/* {post.media_type?.toLowerCase() === "live" && <NewFeedLiveClappr post={post} playerControlRef={playerControlRef} />} */}
      {post.media_type?.toLowerCase() === "live" && <NewFeedLiveFlv post={post} playerControlRef={playerControlRef} />}
    </div>
  );

  const _bodyContent = () => {
    const content = post;
    const contentType = content["media_type"].toString().toUpperCase();

    if (contentType === "JOIN_FLIPFLOP") {
      return <NewFeedSuggestionFriend post={post} />;
    } else {
      return (
        <div>
          {(content.share_users?.length > 0 || content.like_users?.length > 0) && <NewFeedRepostProfileContent key={post.provider_post_id} post={post} />}

          <div className="flex flex-row items-start space-x-2 w-full pb-1">
            {/* dsfsd */}
            {/* <ImageUrlProfile user={post.re_post_user} style={{ width: 50, height: 50 }} className="rounded-full object-cover border shadow-md " isLinkProfile={true} /> */}

            <ImageUrlProfileStoryAndLive user={post.re_post_user} className="shrink-0 w-14 h-14" isLinkProfile={true} tmpRouteUrl={tmpRouteUrl} />

            <div className="flex flex-row justify-between items-center w-full">
              <div className="flex flex-col">
                <div className="flex items-center space-x-2">
                  <Link to={`${RouteUrl.profile}?id=${post?.re_post_user?.id}`} className="text-lg font-bold hover:underline">
                    {post?.re_post_user?.name}
                  </Link>
                  {post.re_post_user?.is_verified === "Yes" && <img src={profileVerified} alt="" style={{ width: 18, height: 18 }} />}

                  <ButtonFollow className="h-[25px]" friendStatus={post.re_post_user?.friend_status} onClick={() => onClickedFollow(post, post.re_post_user?.id.toString())} />
                </div>
                <div className="flex items-center space-x-2">
                  <p className="text-xs text-muted-foreground">
                    {post?.is_sponsored === "Yes"
                      ? "Sponsored"
                      : post?.new_feed_post_type === "suggestion"
                      ? "Suggestion for you"
                      : `${postedTimeAgo(post?.provider_created_datetime)}`}
                  </p>
                </div>
              </div>
            </div>
            {/* Button info */}
            <div className="flex justify-center">
              <div className="flex justify-center items-center space-x-2">
                <Button variant="ghost" size="icon" onClick={(e) => onOpenPostOption(e, post)}>
                  <Ellipsis className="w-6 h-6" />
                </Button>
              </div>
            </div>
          </div>

          {content.media_title && (
            <div className="mt-2">
              <LinkifyText text={content.media_title} target="_blank" />
            </div>
          )}

          {content.re_post?.is_deleted === 1 && <h1>The content is unavailable!</h1>}

          {/* {content.re_post_user ? (
            // <h1 className="line-clamp-2 text-xs text-muted-foreground">
            //   Body Content : {contentType} {post.post_id}
            // </h1>
            <></>
          ) : content.re_post.is_deleted === 1 ? (
            <h1>The content is unavailable!</h1>
          ) : (
            // <h1>Body Content : {contentType} re_post</h1>
            <></>
          )} */}

          <div className="-mx-3">{_renderContent()}</div>

          <NewFeedLinkButton post={post} />

          {/* total like comment share */}
          <div className="py-2">
            <TextReaction post={post} onOpenReactionDialog={onOpenReactionDialog} />
          </div>

          <div className="flex justify-between py-1 border-t border-gray-300">
            <Button variant="ghost" className="space-x-2 flex-1 " onClick={(e: React.MouseEvent) => onClickedLike(post)}>
              <FontAwesomeIcon
                icon={`${post?.is_reaction_like}` === "1" ? solidHeart : outlineHeart}
                size="lg"
                className={`${post?.is_reaction_like}` === "1" ? "text-red-600" : "text-gray-600"}
              />
              <p>{t("Love")}</p>
            </Button>

            <Button
              variant="ghost"
              className="space-x-2 flex-1"
              onClick={(e: React.MouseEvent) => {
                if (post.media_type === "live") {
                  if (playerControlRef.current) {
                    playerControlRef.current.pause();
                  }
                  onOpenPostModal(post.post_id);
                } else {
                  onOpenDialogNewFeedPost(post, videoRef.current, playerControlRef);
                }
              }}
            >
              <FaRegCommentAlt className="text-lg" />
              <p>{t("Comment")}</p>
            </Button>

            <Button
              variant="ghost"
              className="space-x-2 flex-1"
              onClick={(e: React.MouseEvent) => {
                onOpenDialogShare(post);
              }}
            >
              <PiShareFatLight className="text-lg" />
              <p>{t("Share")}</p>
            </Button>
          </div>

          {Service.toInteger(post?.comment_count) > 0 && (
            <div className="py-2 border-t border-gray-300">
              <TotalCommentReaction post={post} onOpenReactionDialog={onOpenReactionDialog} />
            </div>
          )}

          <NewFeedOneComment post={post} onOpenDialogNewFeedPost={(post) => onOpenDialogNewFeedPost(post, videoRef.current)} />
        </div>
      );
    }
  };

  return (
    <Card ref={postRef} className="pt-3 px-3 pb-1 rounded-lg mb-4">
      {_bodyContent()}
    </Card>
  );
};

export default NewFeedPostComponent;

